import {
  Quaternion as ThreeQuaternion,
  Vector3,
  Matrix4,
  Quaternion,
} from 'three';
import type * as zod from 'zod';

import type { CartesianPose } from '@sb/geometry';
import {
  applyRelativeOffset,
  convertEulerPose,
  posesAreEqual,
  tooltipCoordinatesToBaseCoordinates,
  getPlaneOrientation,
  customBaseRotationToQuaternion,
  rotateVectorByQuaternion,
} from '@sb/geometry';
import { error as logError, info, namespace, warn } from '@sb/log';
import type {
  StepTooltip,
  TCPFrame,
  TCPOffsetOption,
} from '@sb/motion-planning';
import { ArmTarget, isSymbolicTooltip } from '@sb/motion-planning';
import {
  getApproachLocations,
  getPickupLocations,
  getPickupLocationsInOrder,
} from '@sb/palletizing/PalletBoxesUtil';
import type { Space, StepFailure } from '@sb/routine-runner';
import { FailureKind } from '@sb/routine-runner';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { Conditional, Expression } from '../../types';
import Step from '../Step';
import {
  resolveMotionChildTooltip,
  resolveTooltipModeFrame,
  savedPoseFrame,
} from '../tooltipResolution';

import Arguments from './Arguments';
import { isCurrentPositionTarget } from './currentPosition';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

const ns = namespace('WaypointStep');
export default class WaypointStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    const { completedCount = 0, stoppedEarly = 0 } =
      this.variablesForInitialization;

    this.variables = {
      completedCount,
      currentActivity: 'none',
      stoppedEarly,
    };
  }

  /**
   * Evaluates the stop early condition if one is configured.
   * Also updates the stoppedEarly variable to track which OR group was met.
   */
  public async evaluateStopEarlyCondition(): Promise<boolean> {
    if (!this.hasStopEarlyConditions()) {
      return false;
    }

    try {
      const { result, matchedBranch } =
        await this.routineFrame.evaluateConditionalWithBranch(
          this.args.stopEarlyConditions!,
        );

      if (result && matchedBranch !== undefined) {
        this.setVariable('stoppedEarly', matchedBranch);
      } else {
        this.setVariable('stoppedEarly', 0);
      }

      return result;
    } catch (err) {
      logError(
        ns`evaluateStopEarlyCondition`,
        `error evaluating stop early condition`,
        err,
      );

      // A force stop that arm-control-bot can monitor is a safety stop, so
      // "could not tell" is not the same as "keep going". The virtual sensor
      // has no realtime monitor to fall back from, so it keeps today's
      // behaviour.
      if (this.hasArmableForceCondition()) {
        throw new Error(
          `Force stop-early condition could not be evaluated: ${
            (err as Error)?.message ?? err
          }. A force stop-early condition that cannot be evaluated must not be read as "do not stop".`,
        );
      }

      // If evaluation fails, don't stop early
      return false;
    }
  }

  /**
   * Walks the stop-early condition tree, reporting whether any operand
   * satisfies `matches`. A conditional operand is searched recursively, so an
   * operand anywhere in the tree counts.
   */
  private someStopEarlyOperand(
    matches: (expression: Expression) => boolean,
  ): boolean {
    const { stopEarlyConditions } = this.args;

    if (!this.hasStopEarlyConditions() || !stopEarlyConditions) {
      return false;
    }

    const checkExpression = (expression: Expression): boolean => {
      if (matches(expression)) {
        return true;
      }

      if (expression.kind === 'conditional') {
        return checkCondition(expression as Conditional);
      }

      return false;
    };

    const checkCondition = (condition: Conditional): boolean =>
      checkExpression(condition.leftOperand) ||
      checkExpression(condition.rightOperand);

    return checkCondition(stopEarlyConditions);
  }

  /**
   * True when any operand reads a force magnitude, whether from the virtual
   * sensor or from force/torque equipment. Drives the faster stop-early poll,
   * which every force source wants.
   */
  public hasForceCondition(): boolean {
    return this.someStopEarlyOperand((expression) => {
      if (
        expression.kind === 'stateVariable' &&
        expression.path.includes('virtualForceTorqueSensorWrench')
      ) {
        return true;
      }

      return WaypointStep.isArmableForceExpression(expression);
    });
  }

  /**
   * True when any operand reads a force source arm-control-bot can threshold
   * in realtime. The virtual force/torque sensor is not one: it is estimated
   * and published, never thresholded, so a condition on it has no realtime
   * path and keeps the poll.
   */
  public hasArmableForceCondition(): boolean {
    return this.someStopEarlyOperand(WaypointStep.isArmableForceExpression);
  }

  private static isArmableForceExpression(expression: Expression): boolean {
    if (
      expression.kind === 'stateVariable' &&
      expression.path.includes('gripperState.forceMagnitude')
    ) {
      return true;
    }

    return (
      expression.kind === 'equipment' && expression.path === 'forceMagnitude'
    );
  }

  /**
   * Returns true if this waypoint has stop early conditions configured
   */
  public hasStopEarlyConditions(): boolean {
    return Boolean(this.args.stopEarlyConditions);
  }

  /**
   * Placeholder method for the play logic.
   * Implement the actual logic here.
   */
  public async _play(): Promise<void> {
    // TODO: Implement the play logic
  }

  // Utility to recursively find a step by id in a tree of steps
  private findStepById(steps: any[], id: string): any | undefined {
    for (const step of steps) {
      if (step.id === id) return step;

      if (step.steps && Array.isArray(step.steps)) {
        const found = this.findStepById(step.steps, id);
        if (found) return found;
      }
    }

    return undefined;
  }

  // Helper to resolve positionListID as either a space item or a waypoint step
  private async resolvePositionListReference(
    positionListID: string,
    positionListIndex: number,
    previousTargetPose: CartesianPose,
  ): Promise<any> {
    if (positionListID === 'current-position') {
      return { pose: previousTargetPose };
    }

    if (
      positionListID === 'expression' &&
      this.args.target &&
      'expression' in this.args.target &&
      this.args.target.expression
    ) {
      const expressionTarget = await this.routineFrame.evaluateExpression(
        this.args.target.expression,
      );

      if (!expressionTarget) {
        logError(ns`resolvePositionListReference`, 'expression target is null');

        throw new Error(
          'Relative waypoint expression evaluated to an invalid value, please check expression',
        );
      }

      if (Array.isArray(expressionTarget)) {
        const list = expressionTarget as any[];

        if (list.length === 0) {
          throw new Error(
            'Relative waypoint expression evaluated to an empty list',
          );
        }

        const idx = positionListIndex % list.length;

        return list[idx];
      }

      return expressionTarget;
    }

    // Try as space item
    try {
      const spaceItem = this.routineFrame.getSpaceItem(positionListID);

      if (spaceItem && Array.isArray(spaceItem.positions)) {
        if (spaceItem.kind === 'palletBoxes') {
          const { palletConfig } = this.args;
          const relevantSpaceItems: Space.Item[] = [];

          if (spaceItem.palletBaseIDs) {
            spaceItem.palletBaseIDs.forEach((palletBaseID) => {
              relevantSpaceItems.push(
                this.routineFrame.getSpaceItem(palletBaseID),
              );
            });
          }

          // Handle pickup position with specific box type
          if (
            palletConfig.moveType === 'pickup' &&
            palletConfig.selectedBoxType
          ) {
            const boxType = spaceItem.boxTypes.find(
              (bt) => bt.id === palletConfig.selectedBoxType,
            );

            if (!boxType) {
              throw new Error(
                `Box type not found: ${palletConfig.selectedBoxType}`,
              );
            }

            // Check if the box type has a pickup position defined
            if (!boxType.pickupPosition) {
              throw new Error(
                `Box type "${boxType.name}" does not have a pickup position defined. Please configure a pickup position for this box type.`,
              );
            }

            return boxType.pickupPosition;
          }

          // Handle approach/box positions with layer and box expressions
          if (
            palletConfig.selectedLayer != null &&
            palletConfig.selectedBox != null
          ) {
            const palletBaseID =
              palletConfig.selectedPalletBaseID || spaceItem.palletBaseIDs[0];

            const boxPickupPositionsMap =
              palletConfig.moveType === 'approach'
                ? getApproachLocations(
                    spaceItem,
                    relevantSpaceItems,
                    palletBaseID,
                  )
                : getPickupLocations(
                    spaceItem,
                    relevantSpaceItems,
                    palletBaseID,
                  );

            const layer = (await this.routineFrame.evaluateExpression(
              palletConfig.selectedLayer,
            )) as number;

            const box = (await this.routineFrame.evaluateExpression(
              palletConfig.selectedBox,
            )) as number;

            if (typeof layer !== 'number' || typeof box !== 'number') {
              throw new Error('Invalid layer or box');
            }

            // Validate layer and box indices
            if (layer < 1 || layer > spaceItem.layerOrder.length) {
              throw new Error(
                `Invalid layer index: ${layer}. Must be between 1 and ${spaceItem.layerOrder.length}`,
              );
            }

            const layerID = spaceItem.layerOrder[layer - 1];

            const layerPattern = spaceItem.layerPatterns.find(
              (lp) => lp.id === layerID,
            );

            if (!layerPattern) {
              throw new Error(
                `Layer pattern not found for layer ID: ${layerID}`,
              );
            }

            if (box < 1 || box > layerPattern.boxList.length) {
              throw new Error(
                `Invalid box index: ${box}. Must be between 1 and ${layerPattern.boxList.length}`,
              );
            }

            const boxID = layerPattern.boxList.find((b) => b.order === box)?.id;

            const key = `${layerID}/${boxID}/${layer - 1}`;
            const boxPickupPosition = boxPickupPositionsMap.get(key);

            if (!boxPickupPosition) {
              throw new Error(
                `No pickup position found for key: ${key}. This suggests a mismatch between the key construction and the map contents.`,
              );
            }

            return boxPickupPosition;
          }

          const palletBaseID =
            palletConfig.selectedPalletBaseID || spaceItem.palletBaseIDs[0];

          const boxPickupPositions = getPickupLocationsInOrder(
            spaceItem,
            relevantSpaceItems,
            palletBaseID,
            palletConfig.moveType === 'approach',
          );

          return boxPickupPositions[
            positionListIndex % boxPickupPositions.length
          ];
        }

        if (spaceItem.positions.length > 0) {
          return spaceItem.positions[
            positionListIndex % spaceItem.positions.length
          ];
        }
      }
    } catch (e) {
      // Not a space item, fall through
    }

    // Try as waypoint step (recursively search the tree). For a Waypoint
    // inside a subroutine, walk the calling sub's spec — not the loaded
    // routine's — so references to sibling waypoints in the same sub
    // resolve. Falls back to the loaded routine for the top-level case.
    const callerRoutineId = this.routineFrame.routineId;

    const rootSteps =
      callerRoutineId === this.routineContext.loadedRoutineState?.id
        ? this.routineContext.loadedRoutineState?.steps ?? []
        : this.routineContext.lookupSubroutine(callerRoutineId)?.steps ?? [];

    const step = this.findStepById(rootSteps, positionListID);

    if (step && step.args) {
      const { args } = step;

      // Check the target structure to determine the type
      if (args.target) {
        // Check if target has positionListID (position list or relative waypoint)
        if ('positionListID' in args.target) {
          // Recursively resolve the position list reference
          return this.resolvePositionListReference(
            args.target.positionListID,
            args.target.positionListIndex ?? positionListIndex,
            previousTargetPose,
          );
        }

        // Check if target has expression
        if ('expression' in args.target) {
          const expressionTarget = await this.routineFrame.evaluateExpression(
            args.target.expression,
            undefined,
            true,
          );

          return Array.isArray(expressionTarget)
            ? expressionTarget[0]
            : expressionTarget;
        }

        // If target has pose or jointAngles, it's a single position
        if ('pose' in args.target || 'jointAngles' in args.target) {
          return args.target;
        }
      }
    }

    throw new Error(
      `Could not resolve positionListID "${positionListID}" as either a space item or a waypoint step`,
    );
  }

  // Helper to evaluate offset expressions and convert to Euler pose
  private async evaluateOffsetExpressions(): Promise<any | null> {
    const { offsetX, offsetY, offsetZ, offsetRx, offsetRy, offsetRz } =
      this.args.relativeConfig;

    // Evaluate all offset expressions
    const [x, y, z, rx, ry, rz] = await Promise.all([
      this.evaluateOffsetExpression(offsetX, true),
      this.evaluateOffsetExpression(offsetY, true),
      this.evaluateOffsetExpression(offsetZ, true),
      this.evaluateOffsetExpression(offsetRx, false),
      this.evaluateOffsetExpression(offsetRy, false),
      this.evaluateOffsetExpression(offsetRz, false),
    ]);

    // Check if any offsets were evaluated
    if (
      x === null &&
      y === null &&
      z === null &&
      rx === null &&
      ry === null &&
      rz === null
    ) {
      return null;
    }

    // Create EulerPose with capitalized property names (use 0 for undefined values)
    return {
      X: x ?? 0,
      Y: y ?? 0,
      Z: z ?? 0,
      Rx: rx ?? 0,
      Ry: ry ?? 0,
      Rz: rz ?? 0,
    };
  }

  // Helper to evaluate a single offset expression
  private async evaluateOffsetExpression(
    expression: any,
    isPositional: boolean,
  ): Promise<number | null> {
    if (!expression) {
      return null;
    }

    try {
      const result = await this.routineFrame.evaluateExpression(expression);

      if (typeof result === 'number') {
        // For positional offsets, convert from the specified distance unit to meters
        if (isPositional && this.args.distanceUnit !== 'meter') {
          return this.convertDistanceToMeters(result, this.args.distanceUnit);
        }

        return result;
      }

      // If result is not a number, try to parse it
      const parsed = parseFloat(String(result));

      if (!Number.isNaN(parsed)) {
        if (isPositional && this.args.distanceUnit !== 'meter') {
          return this.convertDistanceToMeters(parsed, this.args.distanceUnit);
        }

        return parsed;
      }

      return null;
    } catch (error) {
      // If evaluation fails, return null (no offset)
      return null;
    }
  }

  // Helper to convert distance from specified unit to meters
  private convertDistanceToMeters(
    value: number,
    unit: 'meter' | 'millimeter' | 'inch',
  ): number {
    switch (unit) {
      case 'millimeter':
        return value / 1000;
      case 'inch':
        return value * 0.0254;
      case 'meter':
      default:
        return value;
    }
  }

  // Helper to apply an offset pose in the specified frame
  private applyOffsetToPose(pose: any, offsetQuatPose: any, frame: string) {
    switch (frame) {
      case 'base': {
        try {
          const { equipmentItems } = this.routineContext.equipment;

          const customBase = equipmentItems.find(
            (item) => item.config.kind === 'CustomBase' && item.isEnabled,
          );

          if (
            customBase &&
            customBase.config &&
            'rotation' in customBase.config
          ) {
            const quaternion = customBaseRotationToQuaternion(
              customBase.config.rotation,
            );

            // When using a custom base, the robot's coordinate system is rotated
            // So we need to transform the offset to the robot's actual coordinate system
            // before applying it to the pose

            // Transform the offset from world coordinates to robot base coordinates
            const offsetInRobotFrame = rotateVectorByQuaternion(
              { x: offsetQuatPose.x, y: offsetQuatPose.y, z: offsetQuatPose.z },
              quaternion,
            );

            // Create a new offset pose in the robot's coordinate system
            const robotFrameOffset = {
              ...offsetQuatPose,
              x: offsetInRobotFrame.x,
              y: offsetInRobotFrame.y,
              z: offsetInRobotFrame.z,
            };

            // Apply the transformed offset to the pose
            return applyRelativeOffset(pose, robotFrameOffset);
          }

          return applyRelativeOffset(pose, offsetQuatPose);
        } catch (error) {
          this.routineContext.fail({
            failure: { kind: FailureKind.InvalidRoutineLoadedFailure },
            failureReason: 'Invalid custom base configuration',
            error,
          });

          return applyRelativeOffset(pose, offsetQuatPose);
        }
      }
      case 'tool': {
        const poseQ = new ThreeQuaternion(pose.i, pose.j, pose.k, pose.w);
        const posePosition = new Vector3(pose.x, pose.y, pose.z);

        const offsetQuatPoseTooltip =
          tooltipCoordinatesToBaseCoordinates(offsetQuatPose);

        const offsetQ = new ThreeQuaternion(
          offsetQuatPoseTooltip.i,
          offsetQuatPoseTooltip.j,
          offsetQuatPoseTooltip.k,
          offsetQuatPoseTooltip.w,
        );

        const offsetPosition = new Vector3(
          offsetQuatPoseTooltip.x,
          offsetQuatPoseTooltip.y,
          offsetQuatPoseTooltip.z,
        );

        const poseMatrix = new Matrix4()
          .makeRotationFromQuaternion(poseQ)
          .setPosition(posePosition);

        const offsetMatrix = new Matrix4()
          .makeRotationFromQuaternion(offsetQ)
          .setPosition(offsetPosition);

        const finalMatrix = poseMatrix.clone().multiply(offsetMatrix);
        const translation = new Vector3();
        const rotation = new Quaternion();
        const scale = new Vector3();
        finalMatrix.decompose(translation, rotation, scale);

        const poseToolFinal = {
          x: translation.x,
          y: translation.y,
          z: translation.z,
          i: rotation.x,
          j: rotation.y,
          k: rotation.z,
          w: rotation.w,
        } as CartesianPose;

        return poseToolFinal;
      }
      case 'world': {
        return applyRelativeOffset(pose, offsetQuatPose);
      }
      default: {
        // Try to find a plane space item with this frame ID
        try {
          const spaceItem = this.routineFrame.getSpaceItem(frame);

          if (spaceItem.kind === 'plane' && spaceItem.positions.length >= 3) {
            // Create a plane from the space item positions
            const plane = {
              origin: new Vector3(
                spaceItem.positions[0].pose.x,
                spaceItem.positions[0].pose.y,
                spaceItem.positions[0].pose.z,
              ),
              plusX: new Vector3(
                spaceItem.positions[1].pose.x,
                spaceItem.positions[1].pose.y,
                spaceItem.positions[1].pose.z,
              ),
              plusY: new Vector3(
                spaceItem.positions[2].pose.x,
                spaceItem.positions[2].pose.y,
                spaceItem.positions[2].pose.z,
              ),
            };

            // Get the plane's orientation quaternion
            const baseToPlane = getPlaneOrientation(plane);

            // Transform the orientation of the offset to the plane's coordinate system
            const offsetQ = new ThreeQuaternion(
              offsetQuatPose.i,
              offsetQuatPose.j,
              offsetQuatPose.k,
              offsetQuatPose.w,
            );

            // offsetQ now represents rotation baseToPlane * offsetOrientation
            offsetQ.premultiply(baseToPlane);

            // offsetPosition is the offset position in plane coordinates
            // so they need to happen in the plane frame and are
            // decoupled from the offsetQ rotation.
            const offsetPosition = new Vector3(
              offsetQuatPose.x,
              offsetQuatPose.y,
              offsetQuatPose.z,
            );

            // Create transformation matrix to apply offset in plane frame
            const rotMatrixBaseToPlane =
              new Matrix4().makeRotationFromQuaternion(baseToPlane);

            // Translation matrix for the offset position
            const transMatrixOffsetPosition = new Matrix4().makeTranslation(
              offsetPosition.x,
              offsetPosition.y,
              offsetPosition.z,
            );

            // Multiply rotation and translation matrices to get final transformation matrix
            // in global frame to extract translation
            const finalMatrixTranslationOffsetInGlobalFrame =
              rotMatrixBaseToPlane.multiply(transMatrixOffsetPosition);

            const translation = new Vector3();
            const rotation_tmp = new Quaternion();
            const scale_tmp = new Vector3();

            finalMatrixTranslationOffsetInGlobalFrame.decompose(
              translation,
              rotation_tmp,
              scale_tmp,
            );

            // since offsetQ already has baseToPlane applied,
            // we have to take only portion of the rotation that represents
            // the rotation in the base frame.
            const rotation = offsetQ
              .clone()
              .multiply(baseToPlane.clone().invert());

            // Copy the transformed orientation back to the offset pose
            const offsetInPlane = {
              x: translation.x,
              y: translation.y,
              z: translation.z,
              i: rotation.x,
              j: rotation.y,
              k: rotation.z,
              w: rotation.w,
            };

            return applyRelativeOffset(pose, offsetInPlane);
          }
        } catch (e) {
          // Plane not found, fall through to default behavior
        }

        // Default behavior if frame is not recognized or plane not found
        return applyRelativeOffset(pose, offsetQuatPose);
      }
    }
  }

  public async getArmTarget({
    parentCompletedCount,
    previousTargetPose,
    forcePoseGeneration = false,
    // Resolves the read/write frame for an `auto` current-position waypoint.
    // Its presence (`!= null`) is also the gate: it is non-null only when
    // MoveArmToV2 read the `matchRelativeToCurrentFrames` flag on at step start,
    // so the consumption side keys off param-presence (single flag read).
    capturedGlobalTCPOption = undefined,
    // The frame the carried `previousTargetPose` is genuinely expressed in:
    // option name + (when known) the equipment instance that defines it. The
    // gripperID half is what makes `ee-*` names resolvable after a gripper
    // swap — by id, even against disabled equipment.
    previousTargetTCPFrame = undefined,
    // The tooltip the parent pins: a Weld step's torch, or the enclosing Move
    // step's tooltip while constant speed is on. It overrides the waypoint's own
    // selection everywhere — both the read/write frame for current-position and
    // the commanded frame — because the tooltip is defined on the parent, not
    // per waypoint. The stored per-waypoint `tcpOption` is preserved in config
    // but ignored while a pin is in force.
    forcedTooltip = undefined,
    // The robot's Active Tooltip, captured ONCE per parent play cycle so a
    // tooltip written mid-move cannot re-mean the waypoints after it. What
    // `tcpOption: 'active'` (and `'parent'` over an Active-Tooltip Move step)
    // resolves to. Distinct from `capturedGlobalTCPOption`, which is the
    // name-only motion frame.
    activeTooltip = undefined,
    // The enclosing Move step's `stepTooltip`, what `tcpOption: 'parent'`
    // resolves through. Absent means the step predates the field and resolves
    // as `{ kind: 'active' }`.
    parentTooltip = undefined,
  }: {
    parentCompletedCount: number;
    previousTargetPose: CartesianPose;
    forcePoseGeneration?: boolean;
    capturedGlobalTCPOption?: TCPOffsetOption;
    previousTargetTCPFrame?: TCPFrame;
    forcedTooltip?: TCPFrame;
    activeTooltip?: TCPFrame;
    parentTooltip?: StepTooltip;
  }): Promise<
    | {
        target: ArmTarget;
        poseForPreviousTarget: CartesianPose;
        poseForPreviousTargetTCPFrame: TCPFrame | undefined;
      }
    | StepFailure
  > {
    // Hoisted here because the carry-tag rule (joint-match branch) and the
    // read/write frame selection below both key off it.
    const isCurrentPosition = isCurrentPositionTarget(this.args.target);

    // Param-presence = the `matchRelativeToCurrentFrames` flag, read ONCE at
    // MoveArmToV2 step start (never re-read here — a split read could gate
    // plumbing and consumption differently). When the flag is removed, delete
    // this const and the `if` on it below, keeping the body.
    const matchRelativeFramesEnabled = capturedGlobalTCPOption != null;

    // The read/write frame for a current-position waypoint: it reads the
    // carried pose and commands the target in this one frame.
    // `undefined` = not a relative-to-current read (and the entire flag-OFF
    // path: no frame, today's behavior).
    let readWriteFrame: TCPFrame | undefined;

    if (matchRelativeFramesEnabled) {
      // `auto` resolves to the captured global (active tool); a custom selector
      // to its own pick; a parent pin (`forcedTooltip`) wins over both. The
      // gripperID half MUST mirror the `gripperID` the command target ends up
      // with (the manual-pick branch in `parseTarget` sets
      // `this.args.gripperID`; the auto and forced branches set none) so the
      // read resolves through the exact arithmetic command consumption will
      // use.
      if (isCurrentPosition) {
        // A symbolic selection names the read/write frame outright, unless
        // constant speed has forced one. Both halves come from the same
        // resolution the command target uses below, so the read and the
        // command stay matched. No saved frame is available here — the target
        // has not resolved yet, and a relative-to-current waypoint has no
        // stored pose to read one off, so `'auto'` names nothing and falls
        // through.
        const selectionFrame =
          forcedTooltip == null
            ? resolveTooltipModeFrame({
                tcpOption: this.args.tcpOption,
                savedFrame: {},
                activeTooltip,
                parentTooltip,
              })
            : undefined;

        // A symbolic selection that named nothing answers with the captured
        // global, the frame this waypoint reads its carried pose in. Answering
        // anything else — the flange included — would displace the target by
        // the difference between the two tooltips.
        readWriteFrame =
          selectionFrame ??
          forcedTooltip ??
          (isSymbolicTooltip(this.args.tcpOption)
            ? { tcpOption: capturedGlobalTCPOption, gripperID: undefined }
            : {
                tcpOption: this.args.tcpOption,
                gripperID: this.args.gripperID,
              });
      }
    }

    const parseTarget = (
      armPosition: any,
    ): {
      target: ArmTarget;
      pose: CartesianPose;
      tcpFrameTag: TCPFrame | undefined;
    } => {
      const target = {
        motionKind: this.args.motionKind,
        ...armPosition,
        calibrationPose: armPosition.pose,
        // Parent blend config is applied in MoveArmToV2/Step.ts
        blendRadius: this.args.blendConfig.radius,
        stopHere: this.args.stopHere,
        useDeterministicPlanner: this.args.useDeterministicPlanner,
      };

      // Create a copy of the pose to avoid reference issues
      const pose = { ...armPosition.pose };

      // The TCP frame that `pose` (== poseForPreviousTarget for non-Cartesian
      // joint-match) is genuinely expressed in. `undefined` means "frame
      // unknown" — the read below fails loud on it rather than guessing.
      let tcpFrameTag: TCPFrame | undefined;

      if (this.args.shouldMatchJointAngles && !forcePoseGeneration) {
        // If shouldMatchJointAngles is true, we only use joint angles
        target.pose = undefined;

        if (!getFeatureFlagSync('localAccuracyCalibration', false)) {
          target.calibrationPose = undefined;
        }

        // Joint-match: `poseForPreviousTarget` keeps the resolved
        // `armPosition.pose` (Zod strips the now-undefined `pose` key, so the
        // `'pose' in target` carry-out below does NOT overwrite it). The frame
        // that pose was stored in is its stored `tcpOption` + `gripperID` —
        // the teach-time snapshot. The option may be genuinely absent for
        // legacy data (then the tag is `undefined`, NOT a silent `'wrist'`
        // default). The exception is `current-position`: there
        // `armPosition.pose` IS the incoming carry, so the tag is the incoming
        // `previousTargetTCPFrame`.
        if (isCurrentPosition) {
          tcpFrameTag = previousTargetTCPFrame;
        } else if (armPosition.tcpOption) {
          tcpFrameTag = {
            tcpOption: armPosition.tcpOption,
            gripperID: armPosition.gripperID,
          };
        }
      } else {
        // remove jointAngle fields
        target.jointAngles = undefined;
        target.calibrationPose = undefined;

        // Resolved BEFORE `tcpFrameTag` is computed below, so the carry tag
        // names the frame the carry pose is genuinely in. The parent's
        // post-hoc constant-speed override happens after tagging and would
        // otherwise leave a tag naming some other frame.
        const resolved = resolveMotionChildTooltip({
          tcpOption: this.args.tcpOption,
          gripperID: this.args.gripperID,
          savedFrame: savedPoseFrame(armPosition),
          // A Weld step's torch, or the Move step's tooltip while constant
          // speed is on — the child's own selection is locked out and the UI
          // shows "Defined in Weld step" / "Defined in Move step".
          parentForcedTooltip: forcedTooltip,
          readWriteFrame,
          activeTooltip,
          parentTooltip,
        });

        target.effectiveTCPOption = resolved.effectiveTCPOption;
        target.gripperID = resolved.gripperID;

        // Cartesian/tooltip pose produced: the carried pose is interpreted by
        // downstream `subtractEquipmentOffset` in `(effectiveTCPOption,
        // gripperID)`, so that pair is the frame the carry is genuinely in.
        tcpFrameTag = {
          tcpOption: target.effectiveTCPOption,
          gripperID: target.gripperID,
        };
      }

      const parsedTarget = ArmTarget.safeParse(target);

      if (!parsedTarget.success) {
        throw new Error('Move arm target is not valid');
      }

      return { target: parsedTarget.data, pose, tcpFrameTag };
    };

    let target: ArmTarget;
    let anchor: any; // Space Item
    let poseForPreviousTarget: CartesianPose;
    let poseForPreviousTargetTCPFrame: TCPFrame | undefined;

    try {
      if ('positionListID' in this.args.target) {
        const { positionListID, positionListIndex } = this.args.target;
        const index = positionListIndex ?? parentCompletedCount;

        const armPosition = await this.resolvePositionListReference(
          positionListID,
          index,
          previousTargetPose,
        );

        info(ns`getArmTarget`, 'Resolved armPosition (positionListID)', {
          positionListID,
          index,
          pose: armPosition?.pose,
        });

        if (readWriteFrame) {
          // Fail loud on an unknown predecessor frame. If the integrator tag is
          // undefined (a legacy joint-match predecessor that stored no
          // `tcpOption`), the read's from-frame is unknown. A wrong from-frame is
          // a fresh dive of tool-offset magnitude, so we refuse to guess — do NOT
          // fall back to `capturedGlobalTCPOption` (a silent guess that dives if
          // wrong). Reachable only on the flag-on path.
          if (previousTargetTCPFrame == null) {
            // Log the feature-flag detail for our own diagnosis; the
            // operator-facing failureReason stays free of internal flag names.
            warn(
              ns`getArmTarget`,
              'relative-to-current: predecessor waypoint has no stored TCP ' +
                'frame (legacy joint-match data); failing loud. Disable the ' +
                'matchRelativeToCurrentFrames feature flag to revert.',
              { waypointIndex: index },
            );

            return {
              failure: { kind: FailureKind.InvalidRoutineLoadedFailure },
              failureReason:
                'This waypoint moves relative to the current position, but ' +
                "the previous waypoint doesn't record which tooltip it was " +
                "saved with, so the end point of the move can't be " +
                'determined. To fix this, re-save the previous waypoint.',
              error: new Error(
                'relative-to-current: unknown predecessor TCP frame',
              ),
            };
          }

          // Fail loud when the tag's equipment record was deleted outright.
          // The tag is then unresolvable: name-based resolution would borrow
          // the *active* gripper's offset (device kinematics claim foreign
          // `ee-*` names) and dive by the tool-offset difference. A disabled
          // record is fine — resolution below works by id.
          if (
            previousTargetTCPFrame.gripperID != null &&
            !this.routineContext.hasEquipmentDevice(
              previousTargetTCPFrame.gripperID,
            )
          ) {
            warn(
              ns`getArmTarget`,
              'relative-to-current: the predecessor frame names equipment ' +
                'that no longer exists; failing loud. Disable the ' +
                'matchRelativeToCurrentFrames feature flag to revert.',
              {
                waypointIndex: index,
                gripperID: previousTargetTCPFrame.gripperID,
                tcpOption: previousTargetTCPFrame.tcpOption,
              },
            );

            return {
              failure: { kind: FailureKind.InvalidRoutineLoadedFailure },
              failureReason:
                'This waypoint moves relative to the current position, but ' +
                'the previous waypoint was saved with a tooltip that no ' +
                'longer exists on this robot, so the end point of the move ' +
                "can't be determined. To fix this, re-save the previous " +
                'waypoint, or restore the missing equipment.',
              error: new Error(
                'relative-to-current: predecessor TCP frame equipment deleted',
              ),
            };
          }

          // Re-express the carried base pose from the integrator tag's frame
          // (`previousTargetTCPFrame`) into the command frame, BEFORE the
          // relative-offset application below, so the offset lands in the command
          // frame. For current-position `armPosition.pose` IS the incoming carry
          // (resolvePositionListReference returns { pose: previousTargetPose }),
          // so this re-expresses the carry in place. The from-frame is the
          // integrator tag (narrowed to non-null by the guard above).
          armPosition.pose = this.routineContext.reexpressTooltipPose(
            armPosition.pose,
            previousTargetTCPFrame,
            readWriteFrame,
          );
        }

        const parseResult = parseTarget(armPosition);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
        poseForPreviousTargetTCPFrame = parseResult.tcpFrameTag;

        // Anchor logic (optional, only for space items)
        try {
          const targetSpace = this.routineFrame.getSpaceItem(positionListID);

          if (targetSpace.anchoredToID) {
            anchor = this.routineFrame.getSpaceItem(targetSpace.anchoredToID);
          }
        } catch (e) {
          // Not a space item, skip anchor
        }
      } else if ('expression' in this.args.target) {
        const expressionTarget = await this.routineFrame.evaluateExpression(
          this.args.target.expression,
          undefined,
          true,
        );

        // if expression evaluates to an array, use the first item
        // this makes it easier to use 'SinglePosition' space objects.
        const armPosition = Array.isArray(expressionTarget)
          ? expressionTarget[0]
          : expressionTarget;

        const parseResult = parseTarget(armPosition);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
        poseForPreviousTargetTCPFrame = parseResult.tcpFrameTag;
      } else {
        const parseResult = parseTarget(this.args.target);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
        poseForPreviousTargetTCPFrame = parseResult.tcpFrameTag;
      }

      // Check if any offset expressions are present
      const hasOffsets =
        this.args.relativeConfig.offsetX ||
        this.args.relativeConfig.offsetY ||
        this.args.relativeConfig.offsetZ ||
        this.args.relativeConfig.offsetRx ||
        this.args.relativeConfig.offsetRy ||
        this.args.relativeConfig.offsetRz;

      if (
        'pose' in target &&
        hasOffsets &&
        (!this.args.shouldMatchJointAngles || forcePoseGeneration)
      ) {
        // Application of relative configuration offset
        const offsetValues = await this.evaluateOffsetExpressions();

        if (offsetValues) {
          const offsetQuatPose = convertEulerPose.toCartesian(offsetValues);

          // Apply offset to pose for cartesian waypoints
          const offsetResult = this.applyOffsetToPose(
            target.pose,
            offsetQuatPose,
            this.args.relativeConfig.frame,
          );

          if (
            offsetResult &&
            typeof offsetResult === 'object' &&
            'failure' in offsetResult
          ) {
            return {
              failure: {
                kind: FailureKind.InvalidRoutineLoadedFailure,
              },
              failureReason: (offsetResult as any).failureReason,
              error: (offsetResult as any).error,
            };
          }

          target.pose = offsetResult;
        }
      }

      if (
        'positionListID' in this.args.target &&
        anchor &&
        anchor.kind === 'localAccuracyCalibration'
      ) {
        if ('pose' in target) {
          target.pose = this.routineContext.getCalibratedGoal(
            target.pose,
            anchor,
          );
        } else if ('jointAngles' in target && 'calibrationPose' in target) {
          const calibratedPose = this.routineContext.getCalibratedGoal(
            target.calibrationPose,
            anchor,
          );

          // do not change the original target object if pose did not change
          if (!posesAreEqual(target.calibrationPose, calibratedPose)) {
            const newJointAngles =
              await this.routineContext.getJointAnglesForCartesianSpacePose(
                calibratedPose,
                this.args.motionKind,
                false,
              );

            if (newJointAngles) {
              target.jointAngles = newJointAngles;
            } else {
              throw new Error(
                'Offset pose is unreachable: could not solve for joint angles',
              );
            }
          }
        }
      }

      if ('pose' in target) {
        poseForPreviousTarget = target.pose;
      }

      return { target, poseForPreviousTarget, poseForPreviousTargetTCPFrame };
    } catch (error) {
      return {
        failure: {
          kind: FailureKind.InvalidRoutineLoadedFailure,
        },
        failureReason: error.message,
        error,
      };
    }
  }
}
